/* eslint-disable max-lines */
// deps
import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from "react";

// styles
import cx from "classnames";

// internal deps
import {
    AnalyticsContext,
    BLAZE_EVENTS,
    CheckoutActionTypes,
    CheckoutContext,
    ConfirmOrderActionPayload,
    getBridgeSdk,
    getOriginalBusinessLine,
    GlobalHeaderState,
    isPopFlow,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    trackPSRMetricsForWebviewRefresh,
    PSRMetricSource,
    clearDineoutLocalCart,
    DineoutCartContext,
    getDineoutFlowType,
    isJioHotstarFlow,
    DineoutCartType,
    isToingFlow,
} from "@swiggy-private/checkout-core";
import { FreeDeliveryIcon, isEmpty, safeJsonParse, Storage, useEffectOnce } from "@swiggy-private/core-ui";
import { MARKETPLACE_BUSINESS_LINE, OrderData, IMetaData } from "@swiggy-private/payment-types";
import { OfferTag, OfferTagBgClass, TickScreen, TICK_ACCENT, TICK_STATES } from "@swiggy-private/payment-ui";

// hooks
import useConfirmOrder from "@payment-client/hooks/UseConfirmOrder";
import { useTickScreenTransitionInfo } from "@payment-client/hooks/useTickScreenTransitionInfo";

// analytics
import {
    onOrderPlacedEvent,
    onPayOnDeliveryPaymentDoneEvent,
    onSwiggyMoneyRechargeSuccesful,
    paymentConfirmScreenViewEvent,
} from "@payment-client/analytics/events/Payment";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// helpers
import { cleanProcessOrderState, getProcessOrderState } from "@payment-client/helpers/CheckoutStateHelper";
import { logSwiggyOnePurchaseEvent } from "@payment-client/helpers/Clevertap";
import {
    getBusinessLine,
    isBusinessLineDineout,
    isBusinessLinePayOnDelivery,
    isBusinessLineRegular,
    isBusinessLineSwiggyMoney,
    isOriginalBusinessLineSwiggyMoney,
    isBusinessLineGifting,
    clearScenesFullPaymentFlags,
} from "@payment-client/helpers/Misc";
import { getSubTextNodeList } from "@payment-client/helpers/OrderConfirmHelper";
import {
    hasSwiggyOnePurchaseItem,
    isPriorityDeliveryOrder,
    isSuperUserOrder,
    isWebPollingEnabled,
} from "@payment-client/helpers/OrderHelper";
import {
    getChildOrdersCount,
    getConfirmOrderTexts,
    getPaymentCodeFromOrderData,
    getPaymentsCheckoutState,
    getSuccessInfoData,
    isMilestoneUnlockEligible,
    isMroOrderConfirmationExperienceEnabled,
    isPrebookPrepaid,
    isTransitionScreenEnabled,
} from "@payment-client/helpers/PaymentHelper";
import { postOrderEventHelper } from "@payment-client/helpers/PostOrderHelper";
import { storeOrderInfoEvent } from "@payment-client/helpers/StoreOrderHelper";
import { clearOriginalBusinessLine } from "@payment-client/helpers/SwiggyInstrumentsHelper";
import { getTurboPaymentMethodFromPlaceOrder } from "@payment-client/helpers/TurboPayment";
import { getJuspaySdkFeatureAvailability } from "@payment-client/helpers/JuspayHelpers";
import { buildOrderLoadTimePayload } from "@payment-client/helpers/Metrics";
import {
    getMaxLinesForDeliveryAddress,
    getTickScreenTransitionLottieUrl,
    showDeliveryAddress,
} from "@payment-client/helpers/confirmOrder";

// contexts
import { JuspayContext } from "@payment-client/contexts/JuspayContext";

// constants
import { TEXT_TYPES } from "@payment-client/constants/ConfirmScreen";
import {
    Events,
    GirfPayment,
    OFFER_TAG_MILESTONE_TITLE,
    OFFER_TAG_TITLE,
    ORDER_STATUS,
    PreebookPrepaid,
} from "@payment-client/constants/Order";

import {
    DEFAULT_PAYMENTS_RESPONSE_KEY,
    GIFT_DETAILS_STORAGE_KEY,
    PLACE_ORDER_DATA,
    SDK_TURBO_PLACE_ORDER_TIME,
} from "@payment-client/constants/StorageKeys";
import { CLEVERTAP_KEYS } from "@payment-client/constants/Clevertap";

import Blaze from "@swiggy-private/blaze.js";
import { autopayAutoplaceGotoTrackClickEvent } from "@payment-client/analytics/events/PaymentFailed";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { shouldPollForTxnStatus } from "@payment-client/helpers/PollingHelpers";
import styles from "./OrderConfirmSubText.scss";

interface ConfirmOrderContentProps {
    orderId: string;
    orderData: OrderData;
}

// TODO: Consume from config based on order
const POLLING_INTERVAL = 1000; // 1 secs
const POLLING_DURATION = 10000; // 10 secs

export function ConfirmOrder(_props: RouteProps): ReactElement | null {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);

    useEffectOnce(() => {
        getBridgeSdk()
            .setScreenCaptureState({ screenCapturingDisabled: false })
            .catch(() => void 0);
    });

    useEffect(() => {
        if (checkoutState.type === CheckoutActionTypes.CONFIRM_ORDER && !isEmpty(checkoutState.payload)) {
            return;
        }

        const processOrderState = getProcessOrderState();

        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "ConfirmOrderGetState",
                orderId: processOrderState?.orderId || "",
                isEmptyProcessOrderState: String(isEmpty(processOrderState)),
            });
        } catch {
            /* No-op */
        }
        if (processOrderState?.isSwiggyMoney) {
            GlobalHeaderState.updateHeaders({
                marketplacebusinessline: MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY,
                paymentamount: processOrderState?.swiggyMoneyPaymentAmount,
            });
        }

        if (!processOrderState) {
            // Handled Swiggy Money & Regular Payments Checkout State
            const checkoutState = getPaymentsCheckoutState();
            setCheckoutState({ type: checkoutState });
            return;
        }

        const methodName = getPaymentCodeFromOrderData(processOrderState.state?.placeOrderData) || "";

        if (shouldPollForTxnStatus(processOrderState.state.order, methodName)) {
            setCheckoutState({
                type: CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER,
                payload: {
                    order: processOrderState.state.order,
                    placeOrderData: processOrderState.state.placeOrderData,
                    startTime: processOrderState.state.startTime,
                },
            });
            cleanProcessOrderState();
            return;
        }

        setCheckoutState({
            type: CheckoutActionTypes.CONFIRM_ORDER,
            payload: {
                orderId: processOrderState.orderId,
                order: processOrderState.state.order,
                placeOrderData: processOrderState.state.placeOrderData,
            },
        });

        // clean the data from storage
        cleanProcessOrderState();
        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "ConfirmOrderCleanState",
                orderId: getProcessOrderState()?.orderId || "",
                isEmptyProcessOrderState: String(isEmpty(processOrderState)),
            });
        } catch {
            /* No-op */
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (checkoutState.type !== CheckoutActionTypes.CONFIRM_ORDER) {
        return null;
    }

    if (isEmpty(checkoutState.payload)) {
        return null;
    }

    return <ConfirmOrderContent orderId={checkoutState.payload.orderId} orderData={checkoutState.payload.order} />;
}

export const ConfirmOrderContent = memo(function ConfirmOrderContent(
    props: ConfirmOrderContentProps,
): React.ReactElement | null {
    const { impressionEvent, screenViewEvent, clickEvent } = useContext(AnalyticsContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { cartType } = useContext(DineoutCartContext);
    const { billTotal } = useContext(PaymentPayContext);
    const { isSdkAvailable: isJuspaySdkAvailable, featuresAvailability: juspaySdkFeatureAvailable } =
        useContext(JuspayContext);
    const { placeOrderData } = checkoutState.payload as ConfirmOrderActionPayload;

    // TODO: hack for de tipping. revert after reading success messages from api response
    const [isOrderConfirmed, orderSuccessMessage, isPostOrderTip, retryCount] = useConfirmOrder({
        pollingDurationInMs: POLLING_DURATION,
        pollingIntervalInMs: POLLING_INTERVAL,
    });

    const initialState = isOrderConfirmed ? TICK_STATES.SUCCESS : TICK_STATES.LOADING;
    const [state, setState] = useState<TICK_STATES>(initialState);
    const isOTPAnimationApplicable = useMemo(
        () => props.orderData?.isSwiggyBlackOtpApplicable && props.orderData?.isSwiggyBlackOtpAnimationEnabled,
        [props.orderData?.isSwiggyBlackOtpApplicable, props.orderData?.isSwiggyBlackOtpAnimationEnabled],
    );
    const isBoltCartM3 = useMemo(
        () => props.orderData?.isBoltExperienceEnabledOnOrderConfirmationScreen ?? false,
        [props.orderData?.isBoltExperienceEnabledOnOrderConfirmationScreen],
    );

    const isAffordableCartM2 = useMemo(
        () => props.orderData?.isAffordableM2ExperienceEnabledOnOrderConfirmationScreen ?? false,
        [props.orderData?.isAffordableM2ExperienceEnabledOnOrderConfirmationScreen],
    );

    const childOrdersCount = getChildOrdersCount(props.orderData);
    const isMroExperienceEnabledOnOrderConfirmationScreen = isMroOrderConfirmationExperienceEnabled(props.orderData);

    // Logic to handle post confirm order like redirect to track etc
    const postConfirmOrderHandler = useCallback((): void => {
        const orderData = props.orderData;
        const { orderPaymentMethod, orderId, rawResponse, appRedirectionInfo: appRedirectionInfoString } = orderData;
        const isTurboPaymentMethod = getTurboPaymentMethodFromPlaceOrder(placeOrderData);
        const isTransitionScreen = isTransitionScreenEnabled(orderData);

        //Check SDK availblity Based on group name
        const _isJuspaySdkAvailable = getJuspaySdkFeatureAvailability(
            placeOrderData.paymentMethodData.groupName,
            isJuspaySdkAvailable,
            juspaySdkFeatureAvailable,
        );

        recordNREvent(
            {
                eventType: NEWRELIC_TABLES.PAYMENT_WEB_CONFIRM_ORDER,
                method: orderPaymentMethod,
                orderId,
                retryCount,
                isTurboPaymentMethod,
                isTransitionScreen: String(isTransitionScreen),
                webPollingEnabled: isWebPollingEnabled(appRedirectionInfoString).toString(),
                isSdkFlow: _isJuspaySdkAvailable.toString(),
            },
            false,
        );

        try {
            // track PSR metric for confirm order success on webview refresh
            trackPSRMetricsForWebviewRefresh(PSRMetricSource.CONFIRM_ORDER_SUCCESS, {
                method: orderPaymentMethod,
                orderId: String(orderId),
                retryCount: String(retryCount),
            });
        } catch {
            // no-op
        }

        // TODO: remove after IM loader debugging
        Storage.removeItem(PLACE_ORDER_DATA);
        Storage.removeItem(SDK_TURBO_PLACE_ORDER_TIME);

        if (isBusinessLineSwiggyMoney() && isOriginalBusinessLineSwiggyMoney()) {
            onSwiggyMoneyRechargeSuccesful(screenViewEvent);
            clearOriginalBusinessLine();

            const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
            try {
                if (isSessionStorageAvailable && sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY)) {
                    const giftDetails = safeJsonParse(sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY) as string, {});
                    if (giftDetails) {
                        // Update session storage gift-details data with the backend response fields like deeplink, orderId, dateOfPurchase etc.
                        const updatedGiftDetails = {
                            ...giftDetails,
                            shareLink: orderData?.deeplink || "",
                            paymentMode: orderData?.orderPaymentMethod || "",
                            orderId: orderData.orderId,
                            dateOfPurchase: orderData?.confirmationTime || "",
                        };

                        sessionStorage.setItem(GIFT_DETAILS_STORAGE_KEY, JSON.stringify(updatedGiftDetails));
                    }
                }
            } catch {
                void 0;
            }

            postOrderEventHelper(rawResponse.order_data, true);
            return;
        }

        //skip post order flow for swiggy money
        if (isBusinessLineSwiggyMoney()) {
            const originalBusinessLine = getOriginalBusinessLine();
            GlobalHeaderState.updateHeaders({
                paymentamount: "",
                marketplacebusinessline: originalBusinessLine,
            });
            Storage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            setCheckoutState({ type: CheckoutActionTypes.POST_SWIGGY_ADD_MONEY });
            onSwiggyMoneyRechargeSuccesful(screenViewEvent);
            return;
        }

        // dineout payment succes UI
        const showDineOutSuccessScreen = GlobalHeaderState.getHeader("showsuccessscreen") || "";
        if (isBusinessLineDineout()) {
            // Clear scenes full payment flags after successful order confirmation for dineout
            clearScenesFullPaymentFlags();

            if (rawResponse?.order_data) {
                const orderInfo = rawResponse.order_data;
                const metaDataJson = safeJsonParse<IMetaData>(orderInfo?.orders?.[0]?.order_jobs?.[0]?.metadata || "");

                if (
                    (metaDataJson?.dealCategory === "" || metaDataJson?.type === GirfPayment) &&
                    metaDataJson?.dealCategory !== Events &&
                    metaDataJson?.dealCategory !== PreebookPrepaid
                ) {
                    storeOrderInfoEvent(orderInfo, false);
                }
            }
            if (showDineOutSuccessScreen === "true") {
                /* Flow type is required for the dineout cart bridge functions, so that app can identify which cart flow is being used e.g. table reservation, event booking or bill payment */
                /* If we don't pass the flow type, the bridge functions will not be able to identify which cart flow to update/delete 
                /* and this might create issues when the user is using multiple carts in the same app session */

                const flowType = getDineoutFlowType(cartType);
                clearDineoutLocalCart(flowType);

                Blaze.recordStart(BLAZE_EVENTS.RECORD_DO_PAYMENT_SUCCESS_SCREEN_LOAD_TIME);
                setCheckoutState({
                    type: CheckoutActionTypes.DINEOUT_PAYMENT_SUCCESS,
                    payload: { isCheckoutFlow: true }, //to differentiate bw standalone success flow and checkout flow
                });
                return;
            }
        }
        if (isBusinessLinePayOnDelivery()) {
            onPayOnDeliveryPaymentDoneEvent(screenViewEvent);
        } else {
            onOrderPlacedEvent(impressionEvent);
        }

        if (isOrderConfirmed && hasSwiggyOnePurchaseItem(orderData)) {
            logSwiggyOnePurchaseEvent(orderData);
        }

        if (!isEmpty(placeOrderData?.extraParams?.pay_on_delivery)) {
            autopayAutoplaceGotoTrackClickEvent(clickEvent, billTotal);
        }

        postOrderEventHelper(rawResponse.order_data, true);
    }, [
        props.orderData,
        placeOrderData,
        retryCount,
        isOrderConfirmed,
        screenViewEvent,
        setCheckoutState,
        cartType,
        impressionEvent,
        clickEvent,
        billTotal,
        isJuspaySdkAvailable,
        juspaySdkFeatureAvailable,
    ]);

    const { transitionLottieConfig, onTransitionScreenView } = useTickScreenTransitionInfo({
        orderData: props.orderData,
    });

    const isSuper = useCallback((): boolean => {
        return isSuperUserOrder(props.orderData);
    }, [props.orderData]);

    const isPriorityDelivery = useMemo((): boolean => {
        return isPriorityDeliveryOrder(props.orderData);
    }, [props.orderData]);

    const getSuperMessage = useCallback(() => {
        // TODO: hack for de tipping. revert after reading success messages from api response
        if (isPostOrderTip) {
            return;
        }
        let successMessage = props.orderData.successMessage ?? "";
        let successMessageInfo = props.orderData.successMessageInfo ?? "";

        if (orderSuccessMessage) {
            successMessage = orderSuccessMessage.successMessage ?? "";
            successMessageInfo = orderSuccessMessage.successMessageInfo ?? "";
        }
        return {
            successMessage,
            successMessageInfo,
        };
    }, [isPostOrderTip, props.orderData.successMessage, props.orderData.successMessageInfo, orderSuccessMessage]);

    const getSuperMessageV2 = useCallback(() => {
        // TODO: hack for de tipping. revert after reading success messages from api response
        if (isPostOrderTip) {
            return;
        }
        let successMessage = props.orderData.successMessageV2 ?? "";
        let successMessageInfo = props.orderData.successMessageInfoV2 ?? "";

        if (orderSuccessMessage) {
            successMessage = orderSuccessMessage.successMessageV2 ?? "";
            successMessageInfo = orderSuccessMessage.successMessageInfoV2 ?? "";
        }
        return {
            successMessage,
            successMessageInfo,
        };
    }, [isPostOrderTip, props.orderData, orderSuccessMessage]);

    const businessLine = getBusinessLine();

    const isToing = useMemo(() => isToingFlow(), []);

    const isPrebookPrepaidCart = useMemo(() => isPrebookPrepaid(props.orderData), [props.orderData]);
    const successDataInfo = useMemo(() => getSuccessInfoData(props.orderData), [props.orderData]);

    const successData = useMemo(
        () =>
            getConfirmOrderTexts({
                businessLine,
                tickState: state,
                isPostOrderTip,
                isPriorityDelivery: isPriorityDelivery,
                isToing,
                successDataInfo,
                isPrebookPrepaidCart,
            }),
        [businessLine, state, isPostOrderTip, isPriorityDelivery, isToing, cartType, successDataInfo],
    );
    const mainText = successData[TEXT_TYPES.MAIN];
    const subText = successData[TEXT_TYPES.SUB];
    const intermediateText = successData[TEXT_TYPES.INTERMEDIATE];

    const subTextNodeClassName = cx({
        [styles.stealDealSuccessInfo]: cartType === DineoutCartType.STEAL_STORE_CART,
    });

    const subTextNodeList = useMemo(
        () =>
            getSubTextNodeList({
                state,
                subText,
                deliveryAddress: props.orderData.deliveryAddress,
                getSuperMessageCb: getSuperMessage,
                className: subTextNodeClassName,
            }),
        [getSuperMessage, props.orderData.deliveryAddress, state, subText],
    );

    useEffect(() => {
        if (state != TICK_STATES.SUCCESS && isOrderConfirmed) {
            try {
                const isTurboPaymentMethod = getTurboPaymentMethodFromPlaceOrder(placeOrderData);
                //Checking Juspay SDK availiblity based on feature
                const _isJuspaySdkAvailable = getJuspaySdkFeatureAvailability(
                    placeOrderData?.paymentMethodData.groupName,
                    isJuspaySdkAvailable,
                    juspaySdkFeatureAvailable,
                );
                const payload = buildOrderLoadTimePayload({
                    orderData: props.orderData,
                    placeOrderData,
                    isTurboPaymentMethod,
                    _isJuspaySdkAvailable,
                });
                // Record the P2O metrics. From place order to successful confirm order for diffrent payment methods
                Blaze.recordEnd(BLAZE_EVENTS.RECORD_ORDER_LOAD_TIME, {
                    ...payload,
                    orderStatus: ORDER_STATUS.SUCCESS,
                });
            } catch {
                // no-op
            }
            setState(TICK_STATES.SUCCESS);
            paymentConfirmScreenViewEvent(screenViewEvent);
            // This is a native bridge call. This is being used by client to prefetch post order data like gamification
            getBridgeSdk()
                .orderConfirmedEvent(props.orderData?.rawResponse?.order_data ?? {})
                .catch(() => void 0);

            // Get checkoutMetaInfo from bridge SDK
            const businessCategory = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory);
            const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine);

            getBridgeSdk()
                .getCheckoutMetaInfo({ businessCategory, businessLine })
                .then((checkoutMetaInfo) => {
                    if (checkoutMetaInfo && !isEmpty(checkoutMetaInfo?.irctc_data)) {
                        getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.IRCTC_ORDER_CONFIRM, params: {} });
                        // appsflyer events
                        getBridgeSdk().logEvent({ eventName: CLEVERTAP_KEYS.IRCTC_ORDER_CONFIRM, eventParams: {} });
                    }
                })
                .catch(() => void 0);
        }
    }, [isOrderConfirmed, state, props.orderData, screenViewEvent]);
    const customSuccessScreen = GlobalHeaderState.getHeader(SwgyHeaderKeysList.customSuccessScreen) === "true" || false;

    const milestoneUnlocked = useMemo(() => !!isMilestoneUnlockEligible(), []);

    const getAccent = useCallback(() => {
        if (isBusinessLineSwiggyMoney() || isBusinessLinePayOnDelivery()) {
            return TICK_ACCENT.GREEN;
        }
        if (isPriorityDelivery) {
            return TICK_ACCENT.BRICK;
        }
        if (isPrebookPrepaidCart) return TICK_ACCENT.LIGHT_GREEN;
    }, [isPriorityDelivery, isPrebookPrepaidCart]);

    const onTransitionScreenError = useCallback((errorType?: string, error?: unknown, url?: string) => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.TRANSITION_SCREEN_ERROR,
            errorType: String(errorType),
            error: String(error),
            url: String(url),
        });
    }, []);

    const recordTransitionScreenLoadTime = useCallback(
        (endTime: number, domLoadedTime?: number, imageLoadedTime?: number) => {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TRANSITION_SCREEN_LOAD_TIME,
                responseTime: endTime,
                domLoadedTime: domLoadedTime || 0,
                imageLoadedTime: imageLoadedTime || 0,
            });
        },
        [],
    );

    const tagElement = useMemo(() => {
        if (props.orderData.isOfferBundleApplied) {
            return (
                <OfferTag
                    title={OFFER_TAG_TITLE}
                    iconEl={<FreeDeliveryIcon />}
                    bgColor={OfferTagBgClass.OFFER_BUNDLE}
                />
            );
        }
        if (milestoneUnlocked) {
            return (
                <OfferTag
                    title={OFFER_TAG_MILESTONE_TITLE}
                    bgColor={OfferTagBgClass.HABIT_FORMING_MILESTONE_UNLOCKED}
                />
            );
        }
        return null;
    }, [props.orderData.isOfferBundleApplied, milestoneUnlocked]);

    const transitionScreenEnabled = isTransitionScreenEnabled(props.orderData);

    const isSwiggyBlackPlan = useMemo(
        () => props.orderData?.isSwiggyBlackApplicable,
        [props.orderData?.isSwiggyBlackApplicable],
    );

    const isPopCart = useMemo(() => isPopFlow(), []);

    const theme = useTheme();

    return (
        <ThemeProvider theme={theme} colorMode={"light"} design_language={theme.designLanguage}>
            <TickScreen
                tickState={state}
                mainText={mainText}
                subTextNodeList={subTextNodeList}
                intermediateText={intermediateText}
                isSuper={isSuper() && !isBusinessLinePayOnDelivery()}
                isAutoPay={isBusinessLinePayOnDelivery()}
                isSwiggyMoney={isBusinessLineSwiggyMoney()}
                accent={getAccent()}
                postSuccessCb={postConfirmOrderHandler}
                customSuccessScreen={customSuccessScreen}
                showBottomAnimation={isPriorityDelivery}
                tagElement={tagElement}
                isRegular={isBusinessLineRegular()}
                deliveryAddress={props.orderData?.deliveryAddress}
                getSuperMessage={getSuperMessageV2}
                isOfferBundleApplied={props.orderData?.isOfferBundleApplied}
                isMilestoneUnlockEligible={milestoneUnlocked}
                onTransitionScreenError={onTransitionScreenError}
                transitionScreenEnabled={transitionScreenEnabled}
                onTransitionScreenView={onTransitionScreenView}
                recordTransitionScreenLoadTime={recordTransitionScreenLoadTime}
                isSwiggyBlackApplicable={isSwiggyBlackPlan}
                isSwiggyBlackOtpAnimationApplicable={isOTPAnimationApplicable}
                successFooterMessage={props.orderData?.successFooterMessage}
                isPopCart={isPopCart}
                isBoltCartM3={isBoltCartM3}
                isAffordableCartM2={isAffordableCartM2}
                transitionLottieConfig={transitionLottieConfig}
                isGiftingCart={isBusinessLineGifting()}
                isJioHotstarFlow={isJioHotstarFlow()}
                isStealStoreCart={isPrebookPrepaidCart}
                tickScreenLottieUrl={getTickScreenTransitionLottieUrl()}
                maxLinesForDeliveryAddress={getMaxLinesForDeliveryAddress()}
                showDeliveryAddress={showDeliveryAddress()}
                isMroExperienceEnabledOnOrderConfirmationScreen={isMroExperienceEnabledOnOrderConfirmationScreen}
                childOrdersCount={childOrdersCount}
            />
        </ThemeProvider>
    );
});
